import type * as React from 'react';
import { type ChartConfig, ChartContainer } from '@/components/ui/chart';
import { cn } from '@/lib/utils';
import type { ChartSeries } from '@/types/data';
import { ChartLegend } from './chart-legend';

type CartesianLegendPosition = 'none' | 'top' | 'bottom' | 'right';

interface CartesianChartFrameProps {
  children: React.ComponentProps<typeof ChartContainer>['children'];
  config: ChartConfig;
  ariaLabel: string;
  ariaLabelledBy?: string;
  series: ChartSeries[];
  legendPosition: CartesianLegendPosition;
  highlightedSeries: string | null;
  onSeriesHover: (seriesId: string | null) => void;
  yAxisLabel?: string;
  topLegendAlign?: 'center' | 'end';
}

/**
 * Private layout seam shared by Cartesian charts.
 *
 * Axis primitives and marks remain owned by each chart; this module owns only
 * the repeated plot, axis-title, and interactive-legend frame.
 */
function CartesianChartFrame({
  children,
  config,
  ariaLabel,
  ariaLabelledBy,
  series,
  legendPosition,
  highlightedSeries,
  onSeriesHover,
  yAxisLabel,
  topLegendAlign = 'center',
}: CartesianChartFrameProps) {
  const legend = (position: 'top' | 'right') => (
    <ChartLegend
      series={series}
      position={position}
      onSeriesHover={onSeriesHover}
      highlightedSeries={highlightedSeries}
    />
  );

  return (
    <div className="flex h-full min-w-0 gap-1">
      {yAxisLabel && (
        <div className="flex items-center justify-center pb-6">
          <span
            className="whitespace-nowrap text-xs font-medium text-muted-foreground"
            style={{
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
            }}
          >
            {yAxisLabel}
          </span>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        {legendPosition === 'top' && (
          <div
            className={cn(
              'flex pb-2',
              topLegendAlign === 'end' ? 'justify-end' : 'justify-center',
            )}
          >
            {legend('top')}
          </div>
        )}

        <div className="flex min-h-0 flex-1">
          <ChartContainer
            role="img"
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            config={config}
            className="min-h-0 flex-1 aspect-auto"
          >
            {children}
          </ChartContainer>

          {legendPosition === 'right' && (
            <div className="flex items-start ps-3 pt-2">{legend('right')}</div>
          )}
        </div>

        {legendPosition === 'bottom' && (
          <div className="flex justify-center pt-2">{legend('top')}</div>
        )}
      </div>
    </div>
  );
}

CartesianChartFrame.displayName = 'CartesianChartFrame';

export type { CartesianChartFrameProps, CartesianLegendPosition };
export { CartesianChartFrame };
